import * as React from "react";

import {
  UrlStateProvider,
  UrlStateProviderProps,
  useUrlState,
} from "../../../Common";

import { usePlpDataSourceUrlState } from "../plp-data-source-url-state";

const QUERY_PARSING_STRINGIFY_COMMON_ARGUMENTS: UrlStateProviderProps["query_read_parsing_stringify_arguments"] =
  {
    arrayFormat: "comma",
    parseBooleans: true,
  };

export const PlpUrlStateProvider: React.FunctionComponent = (props) => {
  const {
    readers: plpDataSourceReaders,
    isAnyKindOfPlpData,
    isDependentPlpData,
  } = usePlpDataSourceUrlState();

  const nextState: UrlStateProviderProps["nextState"] = (
    previous,
    partial_update
  ) => {
    const deleteOnSight = ["page", "sort"];
    deleteOnSight.forEach((keyToRemove) => {
      delete previous[keyToRemove];
    });

    let thingsToRemove: string[] = [];
    for (const [key] of Object.entries(previous)) {
      if (isAnyKindOfPlpData(key)) {
        thingsToRemove = [...thingsToRemove, ...[key]];
      }
    }
    thingsToRemove.forEach((keyToRemove) => {
      delete previous[keyToRemove];
    });

    if ((partial_update.page as number) < 1) {
      delete partial_update.page;
    }

    return {
      ...previous,
      ...partial_update,
    };
  };
  const nextState_fromAPartialUpdate: UrlStateProviderProps["nextState_fromAPartialUpdate"] =
    (previous, partial_update) => {
      if ((partial_update.page as number) < 1) {
        delete partial_update.page;
        delete previous.page;
      }

      return {
        ...previous,
        ...partial_update,
      };
    };

  const sortDelegate = (a: string, b: string) => {
    // let ignored keys like the search param go first
    const ignoreKey = (key: string) => {
      switch (key) {
        case "q":
          return true;
        default:
          return false;
      }
    };
    if (ignoreKey(a) && ignoreKey(b)) {
      return 0;
    }
    if (ignoreKey(a)) {
      return -1;
    }
    if (ignoreKey(b)) {
      return 1;
    }

    // let dependent extra data go last
    if (isDependentPlpData(a) && isDependentPlpData(b)) {
      return 0;
    }
    if (isDependentPlpData(a)) {
      return 1;
    }
    if (isDependentPlpData(b)) {
      return -1;
    }

    // otherwise just go by standard string compare
    return a.localeCompare(b);
  };

  return (
    <UrlStateProvider
      query_read_parsing_stringify_arguments={{
        ...QUERY_PARSING_STRINGIFY_COMMON_ARGUMENTS,
        ...{
          parseFragmentIdentifier: true,
        },
      }}
      query_update_parsing_stringify_arguments={{
        parseFragmentIdentifier: true,
      }}
      query_update_stringify_arguments={
        QUERY_PARSING_STRINGIFY_COMMON_ARGUMENTS
      }
      sort_delegate={sortDelegate}
      readers={[...plpDataSourceReaders]}
      nextState={nextState}
      nextState_fromAPartialUpdate={nextState_fromAPartialUpdate}
    >
      {props.children || []}
    </UrlStateProvider>
  );
};

export const usePlpUrlState = useUrlState;
